import { useContext, useEffect, useMemo, useState } from 'react'
import { isValidToken } from '../services/auth-utils'
import { useGetSearchConfig } from '../components/aoraki/generic-search-react/src'
import { UserContext } from '../state/user-state'
import * as L from 'leaflet'
import { SearchQuery, SearchTemplate } from '../components/aoraki/generic-search-common/src'
import { ClientCache } from '@msaf/core-common'
import { RequestOptions } from '@msaf/generic-search-common'

interface UseAdvancedSearchProps {
  searchTypeKey: string
}

export const urlPrefix = process.env.REACT_APP_SERVER_ADDRESS?.endsWith('/')
  ? `${process.env.REACT_APP_SERVER_ADDRESS}api`
  : `${process.env.REACT_APP_SERVER_ADDRESS}/api`

// Cache key for the map zoom level
const mapBoundsCacheKey = 'map-bounds'

export default function useAdvancedSearch({ searchTypeKey }: UseAdvancedSearchProps) {
  const { state: user } = useContext(UserContext)
  const [searchTemplate, setSearchTemplate] = useState<SearchTemplate>()
  const requestOptions = useMemo<RequestOptions>(() => {
    if (isValidToken(user?.token) && typeof user.token == 'string' && user?.auth_method === 'Bearer') {
      return {
        urlPrefix,
        authStrategy: 'token',
        authTokenScheme: 'Bearer',
        authToken: user?.token,
      }
    }

    return { authStrategy: 'none', urlPrefix }
  }, [user?.token, user?.auth_method])

  const { isSuccess, data } = useGetSearchConfig(
    searchTypeKey,
    {
      ...requestOptions,
      urlPrefix: `${requestOptions.urlPrefix}/generic-search`,
    },
    !!searchTypeKey,
  )

  useEffect(() => {
    if (isSuccess && data?.searchTemplate) {
      setSearchTemplate(data.searchTemplate)
    }
  }, [isSuccess, data?.searchTemplate])

  // Cache the search query for the active search type key
  const onSearchQueryChange = (query: SearchQuery) => {
    const cacheClient = ClientCache.getInstance()
    const cacheKey = query.searchTypeKey ?? ''
    const cachedQuery = cacheClient.fetch<SearchQuery>(cacheKey)
    if (!cachedQuery || (cachedQuery && JSON.stringify(query) !== JSON.stringify(cachedQuery))) {
      // Cache search query
      cacheClient.create(cacheKey, query)
    }
  }

  const onZoomLevelChange = (bounds: L.LatLngBounds) => {
    const cacheClient = ClientCache.getInstance()
    // Cache map bounds
    cacheClient.create(mapBoundsCacheKey, { northEast: bounds.getNorthEast(), southWest: bounds.getSouthWest() })
  }

  return {
    searchTypeKey,
    searchTemplate,
    userPermissions: user?.permissions,
    requestOptions,
    onSearchQueryChange,
    cachedSearchQuery: ClientCache.getInstance().fetch<SearchQuery>(searchTypeKey),
    onZoomLevelChange,
    cachedMapBounds:
      ClientCache.getInstance().fetch<{ northEast: L.LatLng; southWest: L.LatLng; zoom: number }>(mapBoundsCacheKey),
  }
}
